import React from 'react';

import * as Sentry from '@sentry/browser';
import moment from 'moment';
import Pusher, { Channel } from 'pusher-js';

import { addNotificationAction } from '../action/notifications/notifications';
import { setDataForMessageRefreshDirty } from '../action/set-data-for-messages-refresh-dirty';
import { loadFeatureGate } from '../feature-gates/actions/load-feature-gate';
import { FeatureGate, featureGatesForUsername } from '../feature-gates/feature-gate-selectors';
import { setFeatureUsageTasksDirty } from '../feature-usage-task/actions/set-feature-usage-tasks-dirty';
import { StoreManager } from '../reducers/store-manager';

import { UserApiClient } from '../api/user-api-client';

import { AnonymousSurveyResponseNotification } from './notifications/anonymous-survey-response-notification';
import {
  prepareApplicantMessageNotificationData,
  ApplicantMessageNotification,
} from './notifications/applicant-message-notification';
import {
  prepareEmployeeMessageNotificationData,
  EmployeeMessageNotification,
} from './notifications/employee-message-notification';
import {
  SurveyResponseNotification,
  prepareSurveyResponseNotificationData,
} from './notifications/survey-response-notification';

import { isProd } from '../config/config';
import * as PUSHER from '../config/pusher-config';

export class RealTime {
  /**
   * The instance of Pusher that we use to subscribe to channels. We
   * reinitialize it after the current token expires, which is why it
   * can be null.
   */
  static socket: Pusher;

  static init = (user: AuthenticatedUser): void => {
    // If we've already subscribed, then just return this instance
    if (RealTime.socket) {
      return;
    }

    Pusher.logToConsole = !isProd;

    const { username } = user;

    // IF (07/27/20): Using a custom authorizer
    // https://github.com/pusher/pusher-js#authorizer-function
    RealTime.socket = new Pusher(PUSHER.PUSHER_KEY, {
      authorizer: (channel) => ({
        authorize: (socketId, callback) => {
          new UserApiClient(username)
            .pusherAuth(socketId, channel.name)
            .then((response) => response.json())
            .then((authData) => {
              callback(false, authData);
            })
            .catch((e) => {
              callback(true, e);
            });
        },
      }),
      cluster: PUSHER.PUSHER_CLUSTER,
    });

    // IF (12/13/19): Want to prevent users from having multiple connections if they open
    // multiple tabs. This disconnects/reconnects based on tab visibility.
    document.addEventListener('visibilitychange', () => {
      if (RealTime.socket) {
        if (document.visibilityState === 'visible') {
          StoreManager.getStore().dispatch(setDataForMessageRefreshDirty());
          RealTimeHandlers.reloadExpiredFeatureFlags(user);

          RealTime.socket.connect();
        } else {
          RealTime.socket.disconnect();
        }
      }
    });

    // IF (11/22/19): The private- prefix here is a pusher convention that means the channel requires
    // authentication. We don't want to subscribe admins to a user channel, they get an org channel
    // instead.
    // https://pusher.com/docs/channels/using_channels/private-channels
    if (!user.isAdmin) {
      const channel = RealTime.socket.subscribe(`private-${username}`);
      RealTime.subscribeToEvents(channel);
    }
  };

  static subscribeAdminToOrg = (user: AuthenticatedUser, orgId: number) => {
    if (!user.isAdmin) {
      Sentry.captureMessage('Non-admin tried to subscribe to an org pusher channel');
      return;
    }

    if (!RealTime.socket) {
      RealTime.init(user);
    }

    // IF (07/27/20): Unsubscribe from any org channels currently subscribed to
    const { channels } = RealTime.socket.channels;
    Object.keys(channels)
      .filter((channelName) => channels[channelName].subscribed)
      .forEach((channelName) => RealTime.socket.unsubscribe(channelName));

    // IF (07/27/20): Subscribe to the new org channel
    const channel = RealTime.socket.subscribe(`private-orgId-${orgId}`);
    RealTime.subscribeToEvents(channel);
  };

  static subscribeToEvents = (channel: Channel) => {
    channel.bind('INCOMING_MESSAGE', RealTimeHandlers.handleIncomingMessage);
    channel.bind('INCOMING_SURVEY_RESPONSE', RealTimeHandlers.handleIncomingSurveyResponse);
    channel.bind('SET_REDUX_DIRTY', RealTimeHandlers.handleSetReduxDirty);
  };
}

type IncomingMessageEvent = {
  context: ContactContextType;
  message: Message;
  orgId?: number;
  isAnonymousSurvey?: boolean;
};

class RealTimeHandlers {
  static handleIncomingMessage = async ({ context, message }: IncomingMessageEvent) => {
    const { jobApplicantId, employeeId } = context;

    if (jobApplicantId) {
      await prepareApplicantMessageNotificationData(jobApplicantId, StoreManager.getStore());
      StoreManager.getStore().dispatch(
        addNotificationAction({
          key: jobApplicantId,
          content: (onClose: () => void) => (
            <ApplicantMessageNotification
              onClose={onClose}
              message={message}
              jobApplicantId={jobApplicantId}
            />
          ),
        }),
      );
    } else if (employeeId) {
      await prepareEmployeeMessageNotificationData(employeeId, StoreManager.getStore());
      StoreManager.getStore().dispatch(
        addNotificationAction({
          key: employeeId,
          content: (onClose: () => void) => (
            <EmployeeMessageNotification
              onClose={onClose}
              message={message}
              employeeId={employeeId}
            />
          ),
        }),
      );
    } else {
      Sentry.captureMessage(
        `Invalid context for realtime message (messageId: ${message.messageId}`,
      );
    }
  };

  static handleIncomingSurveyResponse = async ({
    context,
    message,
    orgId,
    isAnonymousSurvey,
  }: IncomingMessageEvent) => {
    const { employeeId } = context;
    let key: number | string = '';
    const { surveyId } = message;

    if (!surveyId) {
      Sentry.captureMessage('Real time survey response was missing surveyId');
      return;
    }
    if (isAnonymousSurvey && !orgId) {
      Sentry.captureMessage('Real time anonymous survey response was missing orgId');
      return;
    }
    if (!isAnonymousSurvey && !employeeId) {
      Sentry.captureMessage('Real time non-anonymous survey response was missing employeeId');
      return;
    }

    if (isAnonymousSurvey) {
      // if there is no employeeId the survey should be anonymous
      // we still need a key to attach to the notification so create a random anonymized id
      const randomHash = Math.random().toString(12).slice(2);
      key = `anon${randomHash}`;
    } else if (employeeId) {
      key = employeeId;
    }

    await prepareSurveyResponseNotificationData(surveyId, StoreManager.getStore());
    StoreManager.getStore().dispatch(
      addNotificationAction({
        key,
        content: (onClose: () => void) => {
          if (isAnonymousSurvey && orgId) {
            return (
              <AnonymousSurveyResponseNotification
                onClose={onClose}
                messageBody={message.messageBody}
                surveyId={surveyId}
                orgId={orgId}
              />
            );
          }
          if (employeeId) {
            return (
              <SurveyResponseNotification
                onClose={onClose}
                message={message}
                employeeId={employeeId}
              />
            );
          }
          Sentry.captureMessage(
            'Real time survey response misconfigured, anonymous survey sent with employeeId or non anonymous survey marked anonymous',
          );
          return null;
        },
      }),
    );
  };

  static handleSetReduxDirty = ({ model, param }: { model: string; param: number }) => {
    let setDirtyAction;
    switch (model) {
      case 'FeatureUsageTask':
        setDirtyAction = setFeatureUsageTasksDirty;
        break;
      default:
        Sentry.captureMessage('Unhandled model sent to pusher for SET_REDUX_DIRTY event');
        return;
    }
    StoreManager.getStore().dispatch(setDirtyAction(param));
  };

  static reloadExpiredFeatureFlags = (user: AuthenticatedUser) => {
    const state = StoreManager.getStore().getState();
    const userFeatureFlags = featureGatesForUsername(state, user.username);
    const now = moment();
    userFeatureFlags.forEach((ff: FeatureGate) => {
      if (moment(ff.ttl).isSameOrBefore(now)) {
        StoreManager.getStore().dispatch(loadFeatureGate(user, ff.gateName));
      }
    });
  };
}
